import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import PersonOutline from '@mui/icons-material/PersonOutline';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { loginUser } from "../redux/slices/authSlice";
import CustomCaptcha from "../services/captcha/CustomCaptcha";
import LoginCarousel from "./LoginCarousel";
import useLoginForm from "../hooks/useLoginForm";
import "./Login.css";

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  const { identifier, password, captchaVerified, errors, setIdentifier, setPassword, setCaptchaVerified, validateForm } = useLoginForm();
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Sign In | RTO Dewas";
    return () => { document.title = previousTitle; };
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loading || submitting.current) return;
    setSubmitted(true);
    const valid = validateForm();
    if (!valid || !captchaVerified) return;
    submitting.current = true;
    try {
      const result = await dispatch(loginUser({ identifier, password }));
      if (loginUser.fulfilled.match(result)) {
        const { role_id } = result.payload;
        sessionStorage.setItem("role_id", role_id);
        navigate("/home");
      }
    } finally {
      submitting.current = false;
    }
  };

  return (
    <Box component="main" className="portal-login">
      <svg className="portal-login-footer-art" aria-hidden="true" viewBox="0 0 1440 120" fill="none"><g stroke="currentColor" strokeWidth="2"><path d="M0 111h1440M16 110V57h72v53M26 57V38h52v19M42 38V23h20v15M116 110V75h68v35M123 75l27-25 27 25M208 110V40h92v70M220 55h12v14h-12zm30 0h12v14h-12zm30 0h10v14h-10zM228 110V88h28v22M1120 110V59h94v51M1133 59V38h67v21M1148 38V22h36v16M1260 110V65h120v45M1271 65V47h96v18M1290 80h16v14h-16zm32 0h16v14h-16zm32 0h16v14h-16z"/><rect x="398" y="71" width="116" height="32" rx="7"/><path d="M408 71V54h77l20 17M418 60h15v11m12-11h15v11m12-11h13v11M352 110V67m-13 12 13-21 14 21m-26 10 12-19 13 19M1025 110V67m-13 12 13-21 14 21m-26 10 12-19 13 19"/><circle cx="420" cy="105" r="7"/><circle cx="491" cy="105" r="7"/><path d="M550 111c150-52 250-52 390 0M570 111c120-30 210-30 340 0"/></g></svg>
      <Box className="portal-login-brand"><LoginCarousel /></Box>
      <Box component="section" className="portal-login-form-section" aria-labelledby="sign-in-title">
        <Paper elevation={0} className="portal-login-card">
          <Typography component="h2" id="sign-in-title" className="portal-login-heading">Welcome Back</Typography>
          <Typography className="portal-login-subtitle">Sign in to access RTO Dewas</Typography>
          <Box component="form" aria-label="Sign in" onSubmit={handleLogin} noValidate aria-busy={loading} className="portal-login-form">
            {error && <Alert severity="error">{typeof error === "string" ? error : error.message || JSON.stringify(error)}</Alert>}
            <TextField className="portal-login-labeled-field" slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start"><PersonOutline fontSize="small" /></InputAdornment> } }} id="login-identifier" label="Email or Mobile" placeholder="Enter email or mobile number" autoComplete="username" fullWidth value={identifier} onChange={(event) => setIdentifier(event.target.value)} error={Boolean(errors.identifier)} helperText={errors.identifier} />
            <TextField className="portal-login-labeled-field" id="login-password" label="Password" placeholder="Enter your password" autoComplete="current-password" type={showPassword ? "text" : "password"} fullWidth value={password} onChange={(event) => setPassword(event.target.value)} error={Boolean(errors.password)} helperText={errors.password} slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start"><LockOutlined fontSize="small" /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton type="button" edge="end" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> } }} />
            <CustomCaptcha onVerify={setCaptchaVerified} requiredError={submitted && !captchaVerified} />
            <FormControlLabel className="portal-login-remember" control={<Checkbox size="small" name="remember" />} label="Remember Me" />
            <Button type="submit" variant="contained" disableElevation fullWidth disabled={loading} className="portal-login-submit" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginIcon fontSize="small" />}>{loading ? "Signing in…" : "Sign In"}</Button>
          </Box>
          <Typography className="portal-login-powered">Powered by <strong>RTO Indore</strong></Typography>
        </Paper>
      </Box>
    </Box>
  );
}
